'use client';

import { Button } from '@/components/ui/button';
import {
    Card,
    CardContent,
    CardDescription,
    CardHeader,
    CardTitle,
} from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { InputGroup, InputGroupAddon, InputGroupInput } from '@/components/ui/input-group';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import VisitorLayout from '@/layouts/visitor-layout';
import { survey } from '@/routes';
import { Head, router, useForm } from '@inertiajs/react';
import { Phone, Star, User } from 'lucide-react';

export default function SurveyPage({
    response,
}: {
    response: {
        customer?: {
            id: number;
            name: string;
            phone: string;
        } | null;
    };
}) {
    const form = useForm({
        name: response.customer?.name ?? '',
        // selectedItems: [],
        notes: '',
        phone: response.customer?.phone ?? '',
        email: '',
        rating: 0,
    });

    const submit = (e: React.FormEvent) => {
        e.preventDefault();

        form.post('/survey', {
            onSuccess: () => {
                form.reset();
                setTimeout(() => {
                    router.visit('/menu');
                }, 2500);
                // router.visit('/menu');
            },
            onError: (errors) => {
                console.error('Form errors:', errors);
            },
        });
    };

    return (
        <VisitorLayout>
            <Head>
                <title>Customer Feedback Survey</title>
                <meta name="description" content="Help us improve by sharing your experience" />
                <meta name="image" content={import.meta.env.VITE_APP_URL + '/logo.jpg'} />
                <meta name="url" content={survey().url} />
                <meta name="canonical_url" content={survey().url} />
                <meta name="type" content="website" />
                <meta name="author" content="Let\'s Cafe" />
                <meta name="robots" content="index, follow" />
                <meta name="published_time" content={new Date().toISOString()} />
                <meta name="modified_time" content={new Date().toISOString()} />
                <meta name="site_name" content="Let\'s Cafe" />
                <meta name="section" content="Customer Feedback Survey" />
                <meta name="tags" content="feedback, survey, improve" />
                <meta name="twitter_username" content="Let\'s Cafe" />
                <meta name="og:title" content="Customer Feedback Survey" />
                <meta name="og:description" content="Help us improve by sharing your experience" />
                <meta name="og:image" content={import.meta.env.VITE_APP_URL + '/logo.jpg'} />
                <meta name="og:url" content={survey().url} />
                <meta name="og:type" content="website" />
                <meta name="og:site_name" content="Let\'s Cafe" />
                <meta name="og:section" content="Customer Feedback Survey" />
                <meta name="og:tags" content="feedback, survey, improve" />
                <meta name="og:twitter_username" content="Let\'s Cafe" />
                <meta name="og:published_time" content={new Date().toISOString()} />
                <meta name="og:modified_time" content={new Date().toISOString()} />
                <meta name="og:locale" content="en" />
                <meta name="og:canonical_url" content={survey().url} />
                <meta name="og:alternates" content={survey().url} />
                <meta name="og:schema" content={survey().url} />
            </Head>
            <div className="flex items-center justify-center p-4">
                <Card className="w-full max-w-2xl">
                    <CardHeader>
                        <CardTitle className="text-3xl">
                            Customer Feedback Survey
                        </CardTitle>
                        <CardDescription>
                            Help us improve by sharing your experience
                        </CardDescription>
                    </CardHeader>

                    <CardContent>
                        {form.recentlySuccessful ? (
                            <div className="py-8 text-center">
                                <div className="mb-4 text-5xl">✓</div>
                                <h3 className="text-xl font-semibold text-primary">
                                    Thank you!
                                </h3>
                            </div>
                        ) : (
                            <form onSubmit={submit} className="space-y-6">
                                <div className='flex gap-6 flex-col md:flex-row'>
                                    {/* Name */}
                                    <div className="space-y-2 flex-1">
                                        <Label
                                            htmlFor="name"
                                            className="flex items-center gap-2"
                                        >
                                            <User className="h-4 w-4" />
                                            Full Name
                                        </Label>
                                        <Input
                                            id="name"
                                            placeholder="John Doe"
                                            value={form.data.name}
                                            onChange={(e) =>
                                                form.setData('name', e.target.value)
                                            }
                                            required
                                        />
                                        {form.errors.name && (
                                            <p className="text-sm text-destructive">
                                                {form.errors.name}
                                            </p>
                                        )}
                                    </div>
                                    <div className="space-y-2 flex-1">
                                        <Label
                                            htmlFor="phone"
                                            className="flex items-center gap-2"
                                        >
                                            <Phone className="h-4 w-4" />
                                            Phone Number
                                        </Label>
                                        <InputGroup>
                                            <InputGroupAddon>+20</InputGroupAddon>
                                            <InputGroupInput
                                                id="phone"
                                                type="tel"
                                                placeholder="1234567890"
                                                value={form.data.phone}
                                                onChange={(e) =>
                                                    form.setData('phone', e.target.value)
                                                }
                                                required
                                            />
                                        </InputGroup>
                                        {form.errors.phone && (
                                            <p className="text-sm text-destructive">
                                                {form.errors.phone}
                                            </p>
                                        )}
                                    </div>
                                </div>

                                {/* Item */}
                                {/* <div className="space-y-2">
                                    <Label>Item (Optional)</Label>
                                    <Select
                                        value={form.data.selectedItems}
                                        onValueChange={(value) =>
                                            form.setData('selectedItems', value)
                                        }
                                    >
                                        <SelectTrigger>
                                            <SelectValue placeholder="Choose an item..." />
                                        </SelectTrigger>
                                        <SelectContent>
                                            {ITEMS.map((item) => (
                                                <SelectItem
                                                    key={item}
                                                    value={item}
                                                >
                                                    {item}
                                                </SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                </div> */}

                                {/* Notes */}
                                <div className="space-y-2">
                                    <Label>Notes</Label>
                                    <Textarea
                                        value={form.data.notes}
                                        onChange={(e) =>
                                            form.setData(
                                                'notes',
                                                e.target.value,
                                            )
                                        }
                                    />
                                </div>

                                {/* Email */}
                                <div className="space-y-2">
                                    <Label>Email</Label>
                                    <Input
                                        type="email"
                                        value={form.data.email}
                                        onChange={(e) =>
                                            form.setData(
                                                'email',
                                                e.target.value,
                                            )
                                        }
                                    />
                                </div>

                                {/* Rating */}
                                <div className="space-y-2">
                                    <Label>Rating</Label>
                                    <div className="flex gap-2">
                                        {[1, 2, 3, 4, 5].map((star) => (
                                            <button
                                                key={star}
                                                type="button"
                                                onClick={() =>
                                                    form.setData('rating', star)
                                                }
                                            >
                                                <Star
                                                    className={
                                                        form.data.rating >= star
                                                            ? 'fill-yellow-400 text-yellow-400'
                                                            : 'text-muted-foreground'
                                                    }
                                                />
                                            </button>
                                        ))}
                                    </div>
                                </div>

                                <Button
                                    type="submit"
                                    className="w-full"
                                    disabled={form.processing}
                                >
                                    {form.processing
                                        ? 'Submitting...'
                                        : 'Submit'}
                                </Button>
                            </form>
                        )}
                    </CardContent>
                </Card>
            </div>
        </VisitorLayout >
    );
}
